<template>
  <div
    id="app"
    style="height:100%;"
  >
    <div
      class="bg-overlay"
      :style="{'z-index': uiModule.isDrawerOpened ? 'auto' : 0}"
      @click="$router.push('/')"
    />
    <div
      class="site-content"
      :style="{'z-index': uiModule.isDrawerOpened ? 'auto' : 0}"
    >
      <NavBar
        v-if="authenticated"
        ref="nav"
      />
      <transition name="el-fade-in-linear">
        <router-view class="w-100" />
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import NavBar from '@/components/Navigation/NavBar.vue'
import UserModule from '@/store/modules/user'
import Vue from 'vue'
import Component from 'vue-class-component'
import UIModule from '@/store/modules/ui'
import { getModule } from 'vuex-module-decorators'
import { FileExportEventQuery, FileExportEventSubscription, FileExportEventKind, AlertConfirmedSubscription, AlertReceiverAction, AlertConfirmedQuery, PackagePndResultQuery, PackagePndResultSubscription, PackagePndResultEvent } from '@/types/graphql'
import { ApolloQueryResult } from 'apollo-client'
import { Watch } from 'vue-property-decorator'
import type { ZenObservable } from 'zen-observable-ts'

@Component({
  components: {
    NavBar
  }
})
export default class extends Vue {
  // State
  private fileExportEventSubscription: ZenObservable.Subscription | null = null
  private alertConfirmedSubscription: ZenObservable.Subscription | null = null
  private packagePndResultSubscription: ZenObservable.Subscription | null = null

  // ➊ Store modules
  private uiModule = getModule(UIModule, this.$store);
  private userModule = getModule(UserModule, this.$store);

  // ➋ Getters
  private get authenticated() {
    return !!this.userModule.isAuthenticated
  }

  // ➌ Hooks
  private mounted() {
    if (this.$refs.nav) {
      this.uiModule.SET_NAV_HEIGHT((this.$refs.nav as Vue).$el.clientHeight)
    }

    this.uiModule.SET_IS_MOBILE(screen.width <= 760)

    if (localStorage.language) {
      this.$i18n.locale = localStorage.language
    }
  }

  // Watchers
  @Watch('authenticated', { immediate: true })
  private onAuthenticatedChanged() {
    if (this.authenticated === true) {
      this.userModule.loadUser()

      if (this.fileExportEventSubscription === null) {
        this.fileExportEventSubscription = this.$apollo.subscribe({
          query: FileExportEventQuery
        }).subscribe({
          next: this.handleFileExportEvent
        })
      }

      if (this.alertConfirmedSubscription === null) {
        this.alertConfirmedSubscription = this.$apollo.subscribe({
          query: AlertConfirmedQuery
        }).subscribe({
          next: this.handleAlertConfirmedEvent
        })
      }

      if (this.packagePndResultSubscription === null) {
        this.packagePndResultSubscription = this.$apollo.subscribe({
          query: PackagePndResultQuery
        }).subscribe({
          next: this.handlePackagePndResultEvent
        })
      }
    } else {
      // Teardown subscriptions on logout
      if (this.fileExportEventSubscription !== null) {
        this.fileExportEventSubscription.unsubscribe()
        this.fileExportEventSubscription = null
      }

      if (this.alertConfirmedSubscription !== null) {
        this.alertConfirmedSubscription.unsubscribe()
        this.alertConfirmedSubscription = null
      }

      if (this.packagePndResultSubscription !== null) {
        this.packagePndResultSubscription.unsubscribe()
        this.packagePndResultSubscription = null
      }
    }
  }

  // Methods
  private handleAlertConfirmedEvent(payload: ApolloQueryResult<AlertConfirmedSubscription>) {
    const alertEvent = payload.data.alertConfirmed
    if (!alertEvent) {
      return
    }

    let alertName = ''

    switch (alertEvent.alert.alert) {
      case 'GENERAL_ERROR': {
        alertName = this.$t('alertReceiver.types.generalError').toString()
        break
      }

      case 'IMPORT_FILE_INGESTED_SUCCESSFULLY': {
        alertName = this.$t('alertReceiver.types.importFileIngestedSuccessfully').toString()
        break
      }

      case 'IMPORT_FILE_INGESTION_ERROR': {
        alertName = this.$t('alertReceiver.types.importFileIngestionError').toString()
        break
      }

      case 'PROD_AGENT_WS_CALL_FAILURE': {
        alertName = this.$t('alertReceiver.types.prodAgentWsCallFailure').toString()
        break
      }

      case 'PACKAGE_LANGUAGE_MISMATCH': {
        alertName = this.$t('alertReceiver.types.packageLanguageMismatch').toString()
        break
      }

      case 'ADDRESS_CORRECTION_NEEDED': {
        alertName = this.$t('alertReceiver.types.addressCorrectionNeeded').toString()
        break
      }

      case 'DOCUMENT_TEMPLATE_CHANGED': {
        alertName = this.$t('alertReceiver.types.documentTemplateChanged').toString()
        break
      }

      case 'OBU_DECLARED_KO': {
        alertName = this.$t('alertReceiver.types.obuDeclaredKo').toString()
        break
      }

      case 'STOCK_ITEM_UPGRADE_FAILURE': {
        alertName = this.$t('alertReceiver.types.stockItemUpgradeFailure').toString()
        break
      }

      case 'PND_PACKAGE_RETURNED': {
        alertName = this.$t('alertReceiver.types.pndPackageReturned').toString()
        break
      }

      case 'PND_PACKAGE_RESEND': {
        alertName = this.$t('alertReceiver.types.pndPackageResend').toString()
        break
      }

      case 'PND_PACKAGE_RECYCLED': {
        alertName = this.$t('alertReceiver.types.pndPackageRecycled').toString()
        break
      }
    }

    switch (alertEvent.action) {
      case AlertReceiverAction.Create: {
        this.$notify({
          title: this.$t('alertReceiver.create.title').toString(),
          message: this.$t('alertReceiver.create.message', { alert: alertName }).toString(),
          type: 'success',
          duration: 5000
        })
        break
      }
      case AlertReceiverAction.Delete: {
        this.$notify({
          title: this.$t('alertReceiver.delete.title').toString(),
          message: this.$t('alertReceiver.delete.message', { alert: alertName }).toString(),
          type: 'success',
          duration: 5000
        })
        break
      }
    }
  }

  private handleFileExportEvent(payload: ApolloQueryResult<FileExportEventSubscription>) {
    switch (payload.data.fileExportEvent?.event) {
      case FileExportEventKind.NoPackagesFoundToExport: {
        this.$notify({
          title: this.$t('fileExportEvent.noPackagesFoundToExport.title').toString(),
          message: this.$t('fileExportEvent.noPackagesFoundToExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.CannotSendMailForPackagesExport: {
        this.$notify({
          title: this.$t('fileExportEvent.cannotSendMailForPackagesExport.title').toString(),
          message: this.$t('fileExportEvent.cannotSendMailForPackagesExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.PackagesExportedSuccessfully: {
        this.$notify({
          title: this.$t('fileExportEvent.packagesExportedSuccessfully.title').toString(),
          message: this.$t('fileExportEvent.packagesExportedSuccessfully.message').toString(),
          type: 'success',
          duration: 5000
        })

        if (payload.data.fileExportEvent.url) {
          window.open(payload.data.fileExportEvent.url, '_blank')
        }

        break
      }
      case FileExportEventKind.PackagesExportFailed: {
        this.$notify({
          title: this.$t('fileExportEvent.packagesExportFailed.title').toString(),
          message: this.$t('fileExportEvent.packagesExportFailed.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.NoImportFilesFoundToExport: {
        this.$notify({
          title: this.$t('fileExportEvent.noImportFilesFoundToExport.title').toString(),
          message: this.$t('fileExportEvent.noImportFilesFoundToExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.CannotSendMailForImportFilesExport: {
        this.$notify({
          title: this.$t('fileExportEvent.cannotSendMailForImportFilesExport.title').toString(),
          message: this.$t('fileExportEvent.cannotSendMailForImportFilesExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.ImportFilesExportedSuccessfully: {
        this.$notify({
          title: this.$t('fileExportEvent.importFilesExportedSuccessfully.title').toString(),
          message: this.$t('fileExportEvent.importFilesExportedSuccessfully.message').toString(),
          type: 'success',
          duration: 5000
        })

        if (payload.data.fileExportEvent.url) {
          window.open(payload.data.fileExportEvent.url, '_blank')
        }

        break
      }
      case FileExportEventKind.ImportFilesExportFailed: {
        this.$notify({
          title: this.$t('fileExportEvent.importFilesExportFailed.title').toString(),
          message: this.$t('fileExportEvent.importFilesExportFailed.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.DashboardDataPackageTypeNotSupported: {
        this.$notify({
          title: this.$t('fileExportEvent.dashboardDataPackageTypeNotSupported.title').toString(),
          message: this.$t('fileExportEvent.dashboardDataPackageTypeNotSupported.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.DashboardDataPackateEntityNotSupported: {
        this.$notify({
          title: this.$t('fileExportEvent.dashboardDataPackageEntityNotSupported.title').toString(),
          message: this.$t('fileExportEvent.dashboardDataPackageEntityNotSupported.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.NoDashboardDataFoundToExport: {
        this.$notify({
          title: this.$t('fileExportEvent.noDashboardDataFoundToExport.title').toString(),
          message: this.$t('fileExportEvent.noDashboardDataFoundToExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.CannotSendMailForDashboardDataExport: {
        this.$notify({
          title: this.$t('fileExportEvent.cannotSendMailForDashboardDataExport.title').toString(),
          message: this.$t('fileExportEvent.cannotSendMailForDashboardDataExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.DashboardDataExportedSuccessfully: {
        this.$notify({
          title: this.$t('fileExportEvent.dashboardDataExportedSuccessfully.title').toString(),
          message: this.$t('fileExportEvent.dashboardDataExportedSuccessfully.message').toString(),
          type: 'success',
          duration: 5000
        })

        if (payload.data.fileExportEvent.url) {
          window.open(payload.data.fileExportEvent.url, '_blank')
        }

        break
      }
      case FileExportEventKind.DashboardDataExportFailed: {
        this.$notify({
          title: this.$t('fileExportEvent.dashboardDataExportFailed.title').toString(),
          message: this.$t('fileExportEvent.dashboardDataExportFailed.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.NoPackageItemsToExport: {
        this.$notify({
          title: this.$t('fileExportEvent.noPackageItemsToExport.title').toString(),
          message: this.$t('fileExportEvent.noPackageItemsToExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.CannotSendMailForPackageItemsExport: {
        this.$notify({
          title: this.$t('fileExportEvent.cannotSendMailForPackageItemsExport.title').toString(),
          message: this.$t('fileExportEvent.cannotSendMailForPackageItemsExport.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
      case FileExportEventKind.PackageItemsExportedSuccessfully: {
        this.$notify({
          title: this.$t('fileExportEvent.packageItemsExportedSuccessfully.title').toString(),
          message: this.$t('fileExportEvent.packageItemsExportedSuccessfully.message').toString(),
          type: 'success',
          duration: 5000
        })

        if (payload.data.fileExportEvent.url) {
          window.open(payload.data.fileExportEvent.url, '_blank')
        }

        break
      }
      case FileExportEventKind.PackageItemsExportFailed: {
        this.$notify({
          title: this.$t('fileExportEvent.packageItemsExportFailed.title').toString(),
          message: this.$t('fileExportEvent.packageItemsExportFailed.message').toString(),
          type: 'error',
          duration: 5000
        })

        break
      }
    }
  }

  private handlePackagePndResultEvent(payload: ApolloQueryResult<PackagePndResultSubscription>) {
    switch (payload.data.packagePndResult?.event) {
      case PackagePndResultEvent.SignalPackageReturned: {
        if (payload.data.packagePndResult.success) {
          this.$notify({
            title: this.$t('packagePndResult.signalPackageReturned.title').toString(),
            message: this.$t('packagePndResult.signalPackageReturned.message', { packageNumber: payload.data.packagePndResult.package.package_number }).toString(),
            type: 'success',
            duration: 5000
          })
        } else {
          this.$notify({
            title: this.$t('packagePndResult.signalPackageReturnFailed.title').toString(),
            message: this.$t('packagePndResult.signalPackageReturnFailed.message', { packageNumber: payload.data.packagePndResult.package.package_number, errorMessage: payload.data.packagePndResult.errorMessage || '' }).toString(),
            type: 'error',
            duration: 5000
          })
        }

        break
      }

      case PackagePndResultEvent.ResendRequest: {
        if (payload.data.packagePndResult.success) {
          this.$notify({
            title: this.$t('packagePndResult.resendRequestAcknowledged.title').toString(),
            message: this.$t('packagePndResult.resendRequestAcknowledged.message', { packageNumber: payload.data.packagePndResult.package.package_number }).toString(),
            type: 'success',
            duration: 5000
          })

          // Emit event for page refresh on PND package view or address correction pages
          const currentPath = this.$route.path
          if (currentPath.includes('/pnd-package/') || currentPath.includes('/address-correction/')) {
            this.$root.$emit('pnd-result-acknowledged', payload.data.packagePndResult.id)
          }
        } else {
          this.$notify({
            title: this.$t('packagePndResult.resendRequestFailed.title').toString(),
            message: this.$t('packagePndResult.resendRequestFailed.message', { packageNumber: payload.data.packagePndResult.package.package_number, errorMessage: payload.data.packagePndResult.errorMessage || '' }).toString(),
            type: 'error',
            duration: 5000
          })
        }

        break
      }

      case PackagePndResultEvent.RecycleRequest: {
        if (payload.data.packagePndResult.success) {
          this.$notify({
            title: this.$t('packagePndResult.recycleRequestAcknowledged.title').toString(),
            message: this.$t('packagePndResult.recycleRequestAcknowledged.message', { packageNumber: payload.data.packagePndResult.package.package_number }).toString(),
            type: 'success',
            duration: 5000
          })

          // Emit event for page refresh on PND package view or address correction pages
          const currentPath = this.$route.path
          if (currentPath.includes('/pnd-package/') || currentPath.includes('/address-correction/')) {
            this.$root.$emit('pnd-result-acknowledged', payload.data.packagePndResult.id)
          }
        } else {
          this.$notify({
            title: this.$t('packagePndResult.recycleRequestFailed.title').toString(),
            message: this.$t('packagePndResult.recycleRequestFailed.message', { packageNumber: payload.data.packagePndResult.package.package_number, errorMessage: payload.data.packagePndResult.errorMessage || '' }).toString(),
            type: 'error',
            duration: 5000
          })
        }

        break
      }
    }
  }
}

</script>

<style lang="scss">

@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700;800;900&family=Roboto:wght@100;300;400;500;700;900&display=swap');

@import "css/transitions.css";
@import "css/home.css";
@import "css/spinner.css";

#app {
  overflow: hidden;
  font-family: "Roboto", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  // text-align: center;
  color: #2c3e50;
}

.el-page-header__content{
  width:100% !important;
}

.address-tooltip {
  height: auto !important;
  min-height: 110px !important;
  width: 440px !important;
}
</style>
